/**
 * Tech-survey respondent schemas (tech-survey Phase 2).
 *
 * Backend: `backend/src/handlers/tech-survey.ts` —
 *   GET /api/opcos/{opcoId}/tech-survey  -> TechSurveyView (definition + this OpCo's answers)
 *   PUT /api/opcos/{opcoId}/tech-survey  -> TechSurveySaveResponse
 *
 * Tolerant boundary (`z.looseObject`) per the qbr/initiatives convention:
 * additive backend fields pass through instead of failing validation.
 */
import { z } from 'zod';

const SurveyOptionSchema = z.looseObject({
  id: z.string(),
  label: z.string(),
  askDetail: z.boolean().optional(),
  exclusive: z.boolean().optional(),
  retired: z.boolean().optional(),
});

const SurveySystemSchema = z.looseObject({
  id: z.string(),
  label: z.string(),
  helpText: z.string().optional(),
  required: z.boolean(),
  multi: z.boolean(),
  options: z.array(SurveyOptionSchema),
});

export const SurveyDefinitionSchema = z.looseObject({
  version: z.number(),
  systems: z.array(SurveySystemSchema),
});

export const SystemAnswerSchema = z.looseObject({
  selected: z.array(z.string()),
  primary: z.string(),
  other: z.string().optional(),
  detail: z.record(z.string(), z.string()).optional(),
  // Server-stamped on save / re-affirm; absent on legacy rows.
  attestedAt: z.string().optional(),
  attestedBy: z.string().optional(),
});

const SurveyStatusSchema = z.looseObject({
  state: z.enum(['complete', 'partial', 'not_started']),
  requiredTotal: z.number(),
  requiredAnswered: z.number(),
  requiredRemaining: z.number(),
  answeredSystemIds: z.array(z.string()),
  firstUnansweredRequiredSystemId: z.string().nullable(),
});

const nullableString = z.string().nullable().optional();

export const TechSurveyViewSchema = z.looseObject({
  opcoId: z.string(),
  opcoName: nullableString,
  operatingGroup: nullableString,
  definition: SurveyDefinitionSchema,
  answers: z.record(z.string(), SystemAnswerSchema).optional().default({}),
  definitionVersion: z.number().nullable().optional(),
  updatedBy: nullableString,
  updatedByName: nullableString,
  updatedAt: nullableString,
  completedAt: nullableString,
  status: SurveyStatusSchema,
});

export const TechSurveySaveResponseSchema = z.looseObject({
  opcoId: z.string(),
  answers: z.record(z.string(), SystemAnswerSchema),
  definitionVersion: z.number().nullable().optional(),
  updatedBy: nullableString,
  updatedByName: nullableString,
  updatedAt: nullableString,
  completedAt: nullableString,
  status: SurveyStatusSchema,
});

// ---------------------------------------------------------------------------
// Admin (Phase 3): report + definition save
//   GET /api/admin/tech-survey/report      -> TechSurveyReport (all in-scope rows; paging/CSV are client-side)
//   PUT /api/admin/tech-survey/definition  -> DefinitionSaveResponse
// ---------------------------------------------------------------------------
// Stored answers are read tolerantly: `primary` may be absent on rule-violating data.
const ReportAnswerSchema = SystemAnswerSchema.partial({ primary: true });

const ReportRowSchema = z.looseObject({
  opcoId: z.string(),
  opcoName: z.string(),
  operatingGroup: z.string(),
  status: z.enum(['complete', 'partial', 'not_started']),
  requiredRemaining: z.number(),
  answers: z.record(z.string(), ReportAnswerSchema),
  updatedAt: nullableString,
  updatedBy: nullableString,
  updatedByName: nullableString,
  completedAt: nullableString,
});

const ReportTotalsSchema = z.looseObject({
  opcos: z.number(),
  complete: z.number(),
  partial: z.number(),
  notStarted: z.number(),
});

const ReportSystemRollupSchema = z.looseObject({
  systemId: z.string(),
  label: z.string(),
  answeredCount: z.number(),
  options: z.array(
    z.looseObject({
      optionId: z.string(),
      label: z.string(),
      retired: z.boolean(),
      primaryCount: z.number(),
      alsoUsesCount: z.number(),
    })
  ),
  other: z.looseObject({
    primaryCount: z.number(),
    alsoUsesCount: z.number(),
    entries: z.array(
      z.looseObject({
        opcoId: z.string(),
        opcoName: z.string(),
        operatingGroup: z.string(),
        text: z.string(),
      })
    ),
  }),
});

export const TechSurveyReportSchema = z.looseObject({
  definition: SurveyDefinitionSchema,
  generatedAt: z.string(),
  totals: ReportTotalsSchema,
  responseRate: z.looseObject({ complete: z.number(), partial: z.number(), total: z.number() }),
  rows: z.array(ReportRowSchema),
  rollups: z.looseObject({
    overall: z.array(ReportSystemRollupSchema),
    byGroup: z.record(
      z.string(),
      z.looseObject({ totals: ReportTotalsSchema, systems: z.array(ReportSystemRollupSchema) })
    ),
  }),
});

export const DefinitionSaveResponseSchema = z.looseObject({
  definition: SurveyDefinitionSchema,
});

export type TechSurveyReport = z.infer<typeof TechSurveyReportSchema>;
export type TechSurveyReportRow = z.infer<typeof ReportRowSchema>;
export type ReportSystemRollup = z.infer<typeof ReportSystemRollupSchema>;
export type DefinitionSaveResponse = z.infer<typeof DefinitionSaveResponseSchema>;

export type SurveyDefinition = z.infer<typeof SurveyDefinitionSchema>;
export type SurveySystem = z.infer<typeof SurveySystemSchema>;
export type SurveyOption = z.infer<typeof SurveyOptionSchema>;
export type SystemAnswer = z.infer<typeof SystemAnswerSchema>;
export type TechSurveyView = z.infer<typeof TechSurveyViewSchema>;
export type TechSurveySaveResponse = z.infer<typeof TechSurveySaveResponseSchema>;
